import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppButton, AppConfirmDialog } from '@pro-sto/ui';
import { authStore, paymentSchemeTemplatesStore } from '@/stores';
import { ROUTES } from '@/constants/routes';
import { formatMoneyRub } from '@/utils/money';
import { PencilIcon, TrashIcon, PlusIcon } from '@/components/icons';
import './PaymentSchemeTemplatesSection.css';

export const PaymentSchemeTemplatesSection: React.FC = observer(() => {
  const navigate = useNavigate();
  const serviceCenterUuid = authStore.user?.service_center_uuid;
  const [templateToDelete, setTemplateToDelete] = useState<{ uuid: string; name: string } | null>(null);

  useEffect(() => {
    if (serviceCenterUuid) {
      paymentSchemeTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
    }
  }, [serviceCenterUuid]);

  const handleDelete = async () => {
    if (!templateToDelete) return;
    const success = await paymentSchemeTemplatesStore.deleteTemplate(templateToDelete.uuid);
    if (success) setTemplateToDelete(null);
  };

  return (
    <div className="payment-scheme-templates-section" data-testid="payment-scheme-templates-section">
      <div className="payment-scheme-templates-section__header">
        <h2 className="payment-scheme-templates-section__title">Шаблоны оплаты</h2>
        <AppButton
          variant="secondary"
          size="M"
          iconRight={<PlusIcon size={16} />}
          onClick={() => navigate(ROUTES.PAYMENT_SCHEME_TEMPLATE_NEW)}
          data-testid="payment-scheme-templates-add"
        >
          Создать шаблон
        </AppButton>
      </div>

      {paymentSchemeTemplatesStore.items.length === 0 ? (
        <p className="payment-scheme-templates-section__empty">Шаблоны ещё не созданы</p>
      ) : (
        <div className="payment-scheme-templates-section__list">
          {paymentSchemeTemplatesStore.items.map((template) => (
            <div className="payment-scheme-templates-section__row" key={template.uuid}>
              <span className="payment-scheme-templates-section__name">{template.name}</span>
              <span className="payment-scheme-templates-section__rate">{formatMoneyRub(template.fix_per_shift)} / смена</span>
              <span className="payment-scheme-templates-section__rate">{formatMoneyRub(template.hourly_rate)} / час</span>
              <span className="payment-scheme-templates-section__count">
                {template.employees_count} {template.employees_count === 1 ? 'сотрудник' : 'сотрудников'}
              </span>
              <div className="payment-scheme-templates-section__actions">
                <button
                  type="button"
                  className="payment-scheme-templates-section__icon-button"
                  onClick={() =>
                    navigate(generatePath(ROUTES.PAYMENT_SCHEME_TEMPLATE_EDIT, { uuid: template.uuid }))
                  }
                  title="Редактировать"
                >
                  <PencilIcon size={20} />
                </button>
                <button
                  type="button"
                  className="payment-scheme-templates-section__icon-button"
                  onClick={() => setTemplateToDelete({ uuid: template.uuid, name: template.name })}
                  title="Удалить"
                  disabled={template.employees_count > 0}
                >
                  <TrashIcon size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AppConfirmDialog
        isOpen={!!templateToDelete}
        title="Удалить шаблон?"
        message={`Вы действительно хотите удалить шаблон «${templateToDelete?.name ?? ''}»?`}
        onConfirm={handleDelete}
        onCancel={() => setTemplateToDelete(null)}
        data-testid="confirm-delete-template-modal"
      />
    </div>
  );
});

PaymentSchemeTemplatesSection.displayName = 'PaymentSchemeTemplatesSection';

export default PaymentSchemeTemplatesSection;
